import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import LoadingIndicator from 'src/components/common/LoadingIndicator'
import { changeEventForm } from 'src/actions/event'
import { eventFormSelector } from 'src/selectors'
import cn from 'classnames'
import SvgIcon from 'components/common/SvgIcon'
import styles from './zazzle-flyer-card.scss'
import CheckCircleIcon from 'src/assets/icons/check-circle.svg'
import { generate16to9ImageWithBlur } from 'src/services/utils'

const ZazzleFlyerCard = forwardRef(({ imageUrl, flyerDetail, containerClassName, showCheckIcon = true }, ref) => {
  const dispatch = useDispatch()
  const eventForm = useSelector(eventFormSelector)
  const [isLoadingFlyer, setIsLoadingFlyer] = useState(true)
  const [blurredImageUrl, setBlurredImageUrl] = useState(null)

  useEffect(() => {
    dispatch(changeEventForm({ mediaType: 'zazzle' }))
  }, [dispatch])

  useEffect(() => {
    let isMounted = true
    setIsLoadingFlyer(true)
    generate16to9ImageWithBlur(imageUrl)
      .then(url => {
        if (isMounted) {
          setBlurredImageUrl(url)
          setIsLoadingFlyer(false)
        }
      })
      .catch(() => {
        if (isMounted) setIsLoadingFlyer(false)
      })
    return () => { isMounted = false }
  }, [imageUrl])

  useImperativeHandle(ref, () => ({
    generateImage: generateCompositeImage
  }))

  const generateCompositeImage = async () => {
    try {
      const flyerBgImageResponse = await fetch(blurredImageUrl)
      const flyerBgImageBlob = await flyerBgImageResponse.blob()
      return flyerBgImageBlob
    } catch (err) {
      return null
    }
  }

  const handleClick = () => {
    const isFlyerSelected = eventForm.flyer_design_number === flyerDetail.design_number
    dispatch(
      changeEventForm({
        flyer_design_number: isFlyerSelected ? null : flyerDetail.design_number,
        flyer_design_slug: isFlyerSelected ? null : flyerDetail.design_slug,
        checkedFlyer: isFlyerSelected ? null : eventForm.checkedFlyer,
        mediaType: isFlyerSelected ? null : 'zazzle'
      })
    )
  }

  return (
    <div className={cn(styles['container'], containerClassName)}>
      <div
        className={cn(styles['flyer-container'], {
          [styles['flyer-container-checked']]:
            eventForm.flyer_design_number === flyerDetail.design_number && !isLoadingFlyer
        })}
        onClick={handleClick}
      >
        {
          showCheckIcon && eventForm.flyer_design_number === flyerDetail.design_number && !isLoadingFlyer && (
            <SvgIcon className={styles['check-icon']} icon={CheckCircleIcon} />
          )
        }

        {
          isLoadingFlyer ? (
            <div className={styles['container']}>
              <div className={styles['loading-container']}>
                <LoadingIndicator />
              </div>
            </div>
          ) : (
            blurredImageUrl && (
              <img
                className={styles['image-inner']}
                src={blurredImageUrl}
                alt="Flyer"
              />
            )
          )
        }
      </div>
    </div>
  )
})

ZazzleFlyerCard.propTypes = {
  containerClassName: PropTypes.string,
  showCheckIcon: PropTypes.bool,
  imageUrl: PropTypes.string,
  flyerDetail: PropTypes.object
}

export default ZazzleFlyerCard
